import { useEffect, useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import AuthLocaleToggle from '@/components/AuthLocaleToggle'
import { useLocaleSync, useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { ApiError, getGoogleLoginUrl, getMicrosoftLoginUrl, loginUser } from '@/services/auth'
import { hasAccessToken } from '@/services/session'
import { createLoginSchema, type LoginFormValues } from './schema'
import LogoRP from '@/components/LogoRP'
import LogoBrSE from '@/components/LogoBrSE'
import SiteFooter from '@/components/SiteFooter'
import { SunIcon, MoonIcon } from '@/components/Icons'

export default function LoginPage() {
  const { t } = useUiStrings()
  useLocaleSync()
  const emailId = useId()
  const passwordId = useId()
  const theme = useStore((s) => s.theme)
  const toggleTheme = useStore((s) => s.toggleTheme)
  const setSession = useStore((s) => s.setSession)
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)

  const loginSchema = useMemo(
    () =>
      createLoginSchema({
        emailRequired: t.emailRequired,
        emailInvalid: t.emailInvalid,
        passwordRequired: t.passwordRequired,
      }),
    [t.emailInvalid, t.emailRequired, t.passwordRequired]
  )

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  if (hasAccessToken()) return <Navigate to="/home" replace />

  const onSubmit = async (data: LoginFormValues) => {
    clearErrors('root')
    try {
      const res = await loginUser(data)
      setSession(
        {
          displayName: res.user.name,
          email: res.user.email,
          plan: res.user.role,
          role: res.user.role,
          authProvider: res.user.authProvider ?? 'local',
        },
        res.accessToken,
        res.refreshToken
      )
      navigate('/home', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        // The server answers a bad email or password in Japanese, so 401 gets
        // the locale's own copy rather than passing that through.
        setError('root', {
          message: err.status === 401 ? t.invalidCredentials : err.message,
        })
      } else {
        setError('root', { message: t.unexpectedError })
      }
    }
  }

  const inputClass = (field: keyof LoginFormValues) =>
    errors[field]
      ? 'input-field border-[var(--error)] focus:border-[var(--error)] focus:ring-[var(--error)]/25'
      : 'input-field'

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <div className="flex flex-1 flex-col items-center justify-center p-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:p-4">
        <div className="w-full max-w-md animate-fade-in rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-[var(--shadow-lg)] sm:p-6 md:p-8">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
            {/* `flex-auto` (not `flex-1`): its basis must reflect the logos'
                actual content width so the row wraps once there's no room for
                both logos plus the locale/theme controls, instead of every
                item shrinking to fit (which clips/squishes the logos — see
                shrink-0 in LogoBrSE.tsx/LogoRP.tsx). `flex-wrap` stays as a
                safety net for narrow mobile widths, but at `sm:` and up the
                logos drop to `h-7` (not grow to `h-10`) specifically so both
                logos + the locale/theme controls fit on one line within the
                card's fixed ~448px width — that combination never fit at
                h-10, wrap or not (both logos + controls need ~448px, the
                card's content area is ~382px). */}
            <div className="flex flex-auto items-center overflow-visible pr-2">
              <LogoBrSE className="h-9 max-h-10 w-auto max-w-full sm:h-7" />
              <LogoRP className="h-9 max-h-10 w-auto max-w-full sm:h-7" />
            </div>
            <div className="flex items-center gap-2">
              <AuthLocaleToggle />
              <button
                type="button"
                onClick={toggleTheme}
                className="rounded-lg p-2 transition-colors hover:bg-[var(--bg-tertiary)]"
                title={theme === 'dark' ? t.lightMode : t.darkMode}
              >
                {theme === 'dark' ? (
                  <SunIcon className="h-[18px] w-[18px] text-[var(--text-secondary)]" />
                ) : (
                  <MoonIcon className="h-[18px] w-[18px] text-[var(--text-secondary)]" />
                )}
              </button>
            </div>
          </div>

          <h1 className="mb-1 text-lg font-bold text-[var(--text-primary)]">{t.login}</h1>
          <p className="mb-6 text-sm text-[var(--text-secondary)]">{t.loginSubtitle}</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor={emailId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.email}
              </label>
              <input
                id={emailId}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={inputClass('email')}
                aria-invalid={errors.email ? 'true' : 'false'}
                {...register('email', { onChange: () => clearErrors('root') })}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor={passwordId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.password}
              </label>
              <div className="relative">
                <input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t.passwordPlaceholder}
                  className={`${inputClass('password')} pr-10`}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  {...register('password', { onChange: () => clearErrors('root') })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                  aria-label={showPassword ? t.hidePassword : t.showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeIcon className="h-4 w-4" />
                  ) : (
                    <EyeOffIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.password.message}</p>
              )}
            </div>

            {errors.root && (
              <p className="text-sm text-[var(--error)]" role="alert">
                {errors.root.message}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary w-full disabled:pointer-events-none disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? t.loggingIn : t.login}
            </button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-[var(--border-color)]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[var(--bg-secondary)] px-2 text-[var(--text-secondary)]">
                  {t.or}
                </span>
              </div>
            </div>

            <a
              href={getGoogleLoginUrl()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
            >
              <GoogleIcon className="h-4 w-4" />
              {t.loginWithGoogle}
            </a>

            <a
              href={getMicrosoftLoginUrl()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
            >
              <MicrosoftIcon className="h-4 w-4" />
              {t.loginWithMicrosoft}
            </a>

            <p className="text-center text-xs text-[var(--text-secondary)]">
              {t.noAccount}{' '}
              <Link
                to="/register"
                className="font-semibold text-[var(--text-primary)] hover:underline"
              >
                {t.register}
              </Link>
            </p>
          </form>
        </div>
      </div>
      <SiteFooter className="bg-[var(--bg-primary)]" />
    </div>
  )
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 23 23"
      className={className}
      aria-hidden="true"
    >
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  )
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  )
}
